import { notifyError } from 'config/setupBugsnag';
import { cartService } from 'services';
import {
  ClientShippingRate,
  CouponClient,
  CreditCardClientData,
  CreditCardsResponse,
  IOrder,
  ShippingRates,
  VerifyRewardPointResponse,
} from 'types';
import RestUtility from '../../restUtility';
import { checkoutEndPoint } from '../../restUtility/restEndpoints';

export class CheckoutService {
  private readonly restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }

  async verifyRewardPoints(shipAddressId: string) {
    try {
      const response = await this.restUtility.put<VerifyRewardPointResponse>(
        `${checkoutEndPoint}/validate_reward_points_available`,
        {
          order: {
            ship_address_id: shipAddressId,
          },
        }
      );
      return response;
    } catch (error) {
      notifyError('ApiError:: verifyRewardPoints', error);
      throw error;
    }
  }

  async saveOrderAddress(
    shipAddressId: string,
    email: string,
    billAddressId?: string
  ) {
    try {
      const response = await this.restUtility.post<IOrder>(
        `${checkoutEndPoint}/add_address`,
        {
          order: {
            bill_address_id: billAddressId,
            ship_address_id: shipAddressId,
            email: email,
          },
          state: 'address',
        }
      );
      return response;
    } catch (error) {
      notifyError('ApiError:: saveOrderAddress', error);
      throw error;
    }
  }

  async getShippingRates() {
    try {
      const response = await this.restUtility.get<ShippingRates>(
        `${checkoutEndPoint}/shipping_rates`
      );
      return this.buildClientShippingRate(response);
    } catch (error) {
      notifyError('ApiError:: getShippingRates', error);
      throw error;
    }
  }

  async getDoctorCreditCards() {
    try {
      const response = await this.restUtility.get<CreditCardsResponse>(
        `${checkoutEndPoint}/get_payment_cards`
      );
      return this.buildClientCreditCardsInfo(response);
    } catch (error) {
      notifyError('ApiError:: getDoctorCreditCards', error);
      throw error;
    }
  }

  buildClientCreditCardsInfo(response: CreditCardsResponse) {
    const creditCards: CreditCardClientData[] = [];
    response.data.forEach(card => {
      const cardLastDigits = card.attributes.card_number.split('-')[1];
      const [expYear, expMonth] = card.attributes.expiry_date.split('-');
      const twoDigitExpYear = expYear.toString().substring(2);
      creditCards.push({
        id: card.id,
        default: card.attributes.default_card,
        cardType: card.attributes.card_type,
        expiry: `${expMonth}/${twoDigitExpYear}`,
        last4Digit: cardLastDigits,
        json_data: card.attributes.json_data,
        name: card.attributes.cardholder_name,
      });
    });
    return {
      creditCards: creditCards,
      isPayByInvoiceEnabled: response.pay_by_invoice_enabled,
    };
  }

  buildClientShippingRate(response: ShippingRates) {
    const selectedShippingRate: ClientShippingRate = {
      shipmentId: '',
      shipmentRateId: '',
      name: '',
      finalPrice: '',
      displayFinalPrice: '',
      isSelected: false,
    };
    try {
      const defaultShippingRate = response.included.filter(
        shipRate =>
          shipRate.type === 'shipping_rate' &&
          shipRate.attributes.selected === true
      )[0];

      selectedShippingRate.shipmentId = defaultShippingRate.id;
      selectedShippingRate.shipmentRateId =
        response.data[0].relationships.shipping_rates.data[0].id;

      selectedShippingRate.name = defaultShippingRate.attributes.name;
      selectedShippingRate.finalPrice =
        defaultShippingRate.attributes.final_price;
      selectedShippingRate.displayFinalPrice =
        defaultShippingRate.attributes.display_final_price;
      selectedShippingRate.isSelected = defaultShippingRate.attributes.selected;
    } catch (error) {
      notifyError('ApiError:: buildClientShippingRate', error);
      throw new Error(
        'Error in shipping cost calculation. Please contact support team.'
      );
    }
    return selectedShippingRate;
  }

  async saveOrderShippingMethod(freeShippingCoupon: CouponClient | undefined) {
    try {
      await this.restUtility.post<IOrder>(
        `${checkoutEndPoint}/add_shipment`,
        {}
      );
    } catch (error) {
      notifyError('ApiError:: saveOrderShippingMethod', error);
      throw error;
    }
    if (freeShippingCoupon && freeShippingCoupon.code) {
      await cartService.applyCouponCode(freeShippingCoupon.code);
    }
  }

  async placeOrder(
    selectedCreditCard: CreditCardClientData | undefined,
    isPayByInvoiceSelected: boolean,
    ipAddress: string,
    pcUsername: string | undefined
  ) {
    try {
      let orderPayload;
      if (isPayByInvoiceSelected) {
        orderPayload = {
          order: {
            payments_attributes: [{ payment_method_id: 'pay_by_invoice' }],
            ip_address: ipAddress,
            pc_username: pcUsername,
          },
          state: 'payment',
        };
      } else {
        orderPayload = {
          order: {
            card_attributes: selectedCreditCard?.json_data,
            ip_address: ipAddress,
            pc_username: pcUsername,
          },
        };
      }
      const response = await this.restUtility.post<IOrder>(
        `${checkoutEndPoint}/complete_order`,
        orderPayload
      );
      return response;
    } catch (error) {
      notifyError('ApiError:: placeOrder', error);
      throw error;
    }
  }
}
